import React, { useEffect, useState } from 'react';
import { Calendar, User, Trophy } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { bookingAPI, membershipAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const MemberPortal = ({ user, onLogout }) => {
  const [bookings, setBookings] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bookingsRes, membershipsRes] = await Promise.all([
          bookingAPI.getAll(),
          membershipAPI.getAll(),
        ]);
        // Filter by user email
        setBookings(
          bookingsRes.data.bookings.filter(
            (b) => b.student_email === user.email || b.parent_email === user.email
          )
        );
        setMemberships(
          membershipsRes.data.memberships.filter(
            (m) => m.swimmer_email === user.email || m.parent_email === user.email
          )
        );
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} onLogout={onLogout} />
        <div className="pt-32 flex items-center justify-center">
          <div className="text-xl text-slate-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} onLogout={onLogout} />

      <div className="pt-32 pb-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-12">
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Welcome, {user.full_name}</h1>
            <p className="text-lg text-slate-600">View your bookings and memberships</p>
          </div>

          {/* My Bookings */}
          <Card className="mb-8" data-testid="my-bookings-section">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center">
                <Calendar className="w-6 h-6 mr-2 text-cyan-700" />
                My Lesson Bookings
              </CardTitle>
            </CardHeader>
            <CardContent>
              {bookings.length === 0 ? (
                <p className="text-slate-600 text-center py-8">No bookings yet</p>
              ) : (
                <div className="space-y-4">
                  {bookings.map((booking) => (
                    <div
                      key={booking.id}
                      className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                      data-testid={`booking-${booking.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900 mb-2">
                            {booking.lesson_type.charAt(0).toUpperCase() + booking.lesson_type.slice(1)} Lesson
                          </h3>
                          <div className="space-y-1 text-slate-600">
                            <p>
                              <strong>Student:</strong> {booking.student_name}
                            </p>
                            <p>
                              <strong>Date:</strong> {booking.lesson_date} at {booking.lesson_time}
                            </p>
                            <p>
                              <strong>Amount:</strong> GHS {booking.amount / 100}
                            </p>
                          </div>
                        </div>
                        <div className="mt-4 md:mt-0 flex flex-col space-y-2">
                          <Badge
                            variant={booking.status === 'confirmed' ? 'default' : 'secondary'}
                            className="self-start md:self-end"
                          >
                            {booking.status}
                          </Badge>
                          <Badge
                            variant={booking.payment_status === 'success' ? 'default' : 'destructive'}
                            className="self-start md:self-end"
                          >
                            {booking.payment_status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* My Memberships */}
          <Card data-testid="my-memberships-section">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center">
                <Trophy className="w-6 h-6 mr-2 text-orange-600" />
                My Team Memberships
              </CardTitle>
            </CardHeader>
            <CardContent>
              {memberships.length === 0 ? (
                <p className="text-slate-600 text-center py-8">No team memberships yet</p>
              ) : (
                <div className="space-y-4">
                  {memberships.map((membership) => (
                    <div
                      key={membership.id}
                      className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                      data-testid={`membership-${membership.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900 mb-2">
                            Marlins Swim Team - {membership.membership_type.charAt(0).toUpperCase() + membership.membership_type.slice(1)}
                          </h3>
                          <div className="space-y-1 text-slate-600">
                            <p>
                              <strong>Swimmer:</strong> {membership.swimmer_name}
                            </p>
                            <p>
                              <strong>Start Date:</strong> {membership.start_date}
                            </p>
                            <p>
                              <strong>Amount:</strong> GHS {membership.amount / 100}
                            </p>
                          </div>
                        </div>
                        <div className="mt-4 md:mt-0 flex flex-col space-y-2">
                          <Badge
                            variant="default"
                            className="self-start md:self-end"
                          >
                            {membership.status}
                          </Badge>
                          <Badge
                            variant={membership.payment_status === 'success' ? 'default' : 'destructive'}
                            className="self-start md:self-end"
                          >
                            {membership.payment_status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MemberPortal;
